<script lang="ts">
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Select from '../ui/Select.svelte';
  import BoardSettings from '../panels/BoardSettings.svelte';
  import { MODE_INFO } from '../content/modes';
  import { DIFFICULTY_PRESETS } from '../content/difficulty';
  import { isUntimedMode, rotatesBoards, soloSetupFields } from '../../shared/modes';
  import type { GameConfig } from '../../shared/config';
  import { getSession } from '../session/context';

  interface Props {
    /** True while this player may still change their mode's setup settings. */
    setupEditable: boolean;
    updateConfig: (patch: Partial<GameConfig>) => void;
  }
  let { setupEditable, updateConfig }: Props = $props();
  const session = getSession();
  // Modes whose player sets the board, timer and word length show the controls instead of facts.
  const boardSettings = $derived(soloSetupFields($session.mode).includes('boardSize'));
</script>

<section id="screen-setup" class="screen submenu">
  <h1>{MODE_INFO[$session.mode].title}</h1>
  <Card>
    {#if !boardSettings}
      <div class="mode-facts">
        <span>{$session.config.boardSize} × {$session.config.boardSize} board</span><span
          >{isUntimedMode($session.mode) ? 'No timer' : `${$session.config.roundDuration} seconds`}</span
        ><span>{$session.config.minWordLength}+ letters</span>
      </div>
    {/if}
    {#if $session.mode === 'practice'}
      <div class="settings settings-grid">
        <label for="practice-difficulty">Difficulty</label>
        <Select
          id="practice-difficulty"
          aria-label="board difficulty"
          placeholder="Custom"
          value={$session.config.difficulty}
          disabled={!setupEditable || $session.autoStart}
          onchange={(value) => updateConfig({ difficulty: value })}
          options={DIFFICULTY_PRESETS}
        />
      </div>
    {/if}
    <p class="mode-description">{MODE_INFO[$session.mode].rules}</p>
    {#if boardSettings}
      <div class="settings settings-grid board-settings">
        <BoardSettings
          config={$session.config}
          editable={setupEditable && !$session.autoStart}
          change={updateConfig}
          durationLabel={rotatesBoards($session.mode) ? 'Board Time' : 'Round Time'}
        />
      </div>
    {/if}
    <Button
      variant="primary"
      size="big"
      full
      disabled={$session.autoStart}
      onclick={() => session.playSetup()}
      >{$session.autoStart ? 'Getting Ready…' : `Play ${MODE_INFO[$session.mode].title}`}</Button
    >
    <p class="status" role="status">{$session.joinStatus}</p>
  </Card>
</section>

<style>
  .mode-facts {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .mode-facts span {
    border: var(--theme-card-border);
    border-radius: var(--theme-chip-radius);
    padding: 6px 10px;
    font-size: 12px;
  }
  .mode-description {
    line-height: 1.7;
    margin: 8px 0;
  }
</style>
